import { Loader2, ShieldCheck } from "lucide-react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import styles from "../../styles/applicationDetail.module.css";
import {
  formatVerifastAcceptanceStatus,
  formatVerifastIdStatus,
} from "../../utils/signaturePaymentHelpers";
import type { ApplicationDetailData, ApplicationLayerData } from "../../types/application";
import { useVerifastLogs } from "../../hooks/useVerifastLogs";
import { VerifastIdentityLogsModal } from "../modals/VerifastIdentityLogsModal";

type VerifastIdentitySectionProps = {
  applicationAllData: ApplicationDetailData;
  applicationData: ApplicationLayerData;
  applicantId: string;
};

function readField(value: unknown): string {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "—";
  }
  return String(value);
}

export function VerifastIdentitySection({
  applicationAllData,
  applicationData,
  applicantId,
}: VerifastIdentitySectionProps) {
  const verifastLogs = useVerifastLogs({
    applicantId,
    hasVerifastSteps: Boolean(applicationAllData?.has_verifast_steps),
  });

  const idStatus = formatVerifastIdStatus(
    applicationData.verifast_id_verify_status ??
      applicationData.verifast_id_status,
  );
  const completionStatus = readField(applicationData.verifast_completion_status);
  const approvalStatus = formatVerifastAcceptanceStatus(
    applicationData.verifast_approval_status ??
      applicationData.verifast_acceptance_status,
  );
  const documentType = readField(applicationData.goverment_id_type);

  const showSection =
    Boolean(applicationAllData?.has_verifast_steps) ||
    idStatus !== "" ||
    completionStatus !== "—" ||
    approvalStatus !== "Pending" ||
    documentType !== "—";

  if (!showSection) return null;

  return (
    <>
      <div
        className={styles.section}
        data-testid="application-signature-payment-tab-section-verifast-identity"
      >
        <div className={`${styles.sectionHeader} ${styles.bankLinkingSectionHeader}`}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <span className={styles.sectionTitleText}>
              Identity Verification (Verifast)
            </span>
          </h2>
          {verifastLogs.showViewLogsButton ? (
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                data-testid="application-signature-payment-tab-view-verifast-logs-button"
                onClick={() => void verifastLogs.openVerifastLogs()}
                disabled={!applicantId || verifastLogs.logsLoading}
                aria-busy={verifastLogs.logsLoading}
                className="inline-flex items-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
              >
                {verifastLogs.logsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : null}
                View Verifast Logs
              </button>
            </div>
          ) : null}
        </div>

        <div className={styles.infoRow}>
          <div className={styles.infoColumn}>
            <h6>ID Status</h6>
            <p>{idStatus || "—"}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6>Completion</h6>
            <p>{completionStatus}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6>Approval</h6>
            <p>{approvalStatus}</p>
          </div>
          <div className={styles.infoColumn}>
            <h6>Document Type</h6>
            <p>{documentType}</p>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={verifastLogs.emptyLogsDialogOpen}
        title="Verifast identity logs"
        message="No Verifast events were found for this applicant."
        primaryLabel="OK"
        secondaryLabel=""
        onPrimary={() => verifastLogs.setEmptyLogsDialogOpen(false)}
        onSecondary={() => verifastLogs.setEmptyLogsDialogOpen(false)}
        onDismiss={() => verifastLogs.setEmptyLogsDialogOpen(false)}
        overlayDataTestId="application-signature-payment-tab-verifast-logs-empty"
        primaryButtonDataTestId="application-signature-payment-tab-verifast-logs-empty-ok-button"
        closeButtonDataTestId="application-signature-payment-tab-verifast-logs-empty-close-button"
      />

      <VerifastIdentityLogsModal
        isOpen={verifastLogs.logsModalOpen}
        onClose={verifastLogs.closeLogsModal}
        applicantId={applicantId}
        initialSessions={verifastLogs.logSessions}
      />
    </>
  );
}
